import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, AlertCircle, Info, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { canOpenPathInAnyCompany } from '../../lib/permissions/permissionService';
import { supabase } from '../../lib/supabase';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  NotificationRow,
} from '../../lib/queries/notifications';
import { formatDistanceToNow } from '../../lib/utils/dateUtils';

const TYPE_STYLES: Record<NotificationRow['type'], { icon: React.ComponentType<{ className?: string }>; color: string; bg: string }> = {
  info:    { icon: Info,          color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  success: { icon: CheckCircle,   color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  warning: { icon: AlertTriangle, color: 'text-amber-800 dark:text-amber-400',  bg: 'bg-amber-100 dark:bg-amber-500/10' },
  error:   { icon: AlertCircle,   color: 'text-red-400',    bg: 'bg-red-500/10' },
};

export default function NotificationBell() {
  const { t } = useTranslation();
  const { profile, user } = useAuth();
  const { companies } = useCompany();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>({});
  const ref = useRef<HTMLDivElement>(null);

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', profile?.id],
    queryFn: () => getNotifications(profile!.id),
    enabled: !!profile?.id,
    refetchInterval: 30000,
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel(`notifications:${profile.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${profile.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', profile.id] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile?.id, queryClient]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const readMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', profile?.id] }),
  });

  const readAllMutation = useMutation({
    mutationFn: () => markAllAsRead(profile!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', profile?.id] }),
  });

  // Stopgap for FIX-LEAVE-NOTIF-1/2: a recipient can be correctly notified about
  // something without holding the permission its linked page requires, which
  // would otherwise send them into a 403. Checked across every company the
  // user belongs to (not just the active one) so a user in Consolidated View
  // isn't wrongly treated as having no access to anything. Resolved lazily
  // per notification and cached in accessMap; defaults to true (openable)
  // until resolved, so this never blocks a click before the check lands.
  useEffect(() => {
    if (!user?.id) return;
    const companyIds = companies.map(c => c.id);
    const urls = [...new Set(notifications.map(n => n.action_url).filter((u): u is string => !!u))];
    urls.forEach(url => {
      if (url in accessMap) return;
      canOpenPathInAnyCompany(url, user.id, companyIds).then(ok => {
        setAccessMap(prev => (url in prev ? prev : { ...prev, [url]: ok }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, user?.id, companies]);

  const canOpen = (n: NotificationRow) => !n.action_url || accessMap[n.action_url] !== false;

  const handleNotificationClick = (n: NotificationRow) => {
    if (!n.is_read) readMutation.mutate(n.id);
    // A recipient can be legitimately notified about something without holding
    // the permission its page requires (e.g. a manager notified about a leave
    // request they may not have hr.leave.view for) — in that case, just let
    // them view the notification itself rather than navigating into a 403.
    if (n.action_url && canOpen(n)) {
      navigate(n.action_url);
      setOpen(false);
    }
  };

  const recent = notifications.slice(0, 8);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 end-1 min-w-[16px] h-4 px-0.5 bg-blue-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 top-full mt-2 w-96 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-300 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <h3 className="text-slate-900 dark:text-white font-semibold text-sm">{t('notif.notifications')}</h3>
              {unreadCount > 0 && (
                <span className="bg-blue-500/20 text-blue-400 text-xs font-medium px-1.5 py-0.5 rounded-full">
                  {t('notif.new_count', { count: unreadCount })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={() => readAllMutation.mutate()}
                  className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white px-2 py-1 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  {t('notif.all_read')}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-slate-600 dark:text-slate-500" />
                </div>
                <p className="text-slate-600 dark:text-slate-500 text-sm">{t('notif.no_notifications')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-300/50 dark:divide-slate-700/50">
                {recent.map(n => {
                  const style = TYPE_STYLES[n.type] ?? TYPE_STYLES.info;
                  const Icon = style.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className={`w-full text-start px-4 py-3 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-all flex items-start gap-3 ${!n.is_read ? 'bg-slate-200/20 dark:bg-slate-700/20' : ''}`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${style.bg} flex items-center justify-center mt-0.5`}>
                        <Icon className={`w-4 h-4 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium leading-snug ${n.is_read ? 'text-slate-600 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-xs text-slate-700 dark:text-slate-600 mt-1">{formatDistanceToNow(n.created_at)}</p>
                      </div>
                      {n.is_read && (
                        <Check className="w-3.5 h-3.5 text-slate-700 dark:text-slate-600 flex-shrink-0 mt-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-slate-300 dark:border-slate-700">
            <button
              onClick={() => { navigate('/notifications'); setOpen(false); }}
              className="w-full text-center text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
            >
              {t('notif.view_all')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
